import { useState, useMemo, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { ChevronDown, Search, Check, X, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const TimezoneSelector = ({
    value,
    onChange,
    label = 'Zaman Dilimi',
    direction = 'bottom',
    required = false,
    error,
}) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const containerRef = useRef(null);

    const timezoneOptions = useMemo(() => {
        try {
            return Intl.supportedValuesOf('timeZone').map(tz => ({
                id: tz,
                label: tz.replace(/_/g, ' '),
            }));
        } catch (e) {
            return [{ id: 'UTC', label: 'UTC' }];
        }
    }, []);

    useEffect(() => {
        if (!value) {
            try {
                const systemTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
                if (systemTz) onChange?.(systemTz);
            } catch (e) { /* ignore */ }
        }
    }, []);

    const filteredTimezones = timezoneOptions.filter(tz =>
        tz.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const selectedTz = timezoneOptions.find(tz => tz.id === value);

    const handleClear = (e) => {
        e.stopPropagation();
        onChange?.(null);
        setIsOpen(false);
        setIsFocused(false);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
                setIsFocused(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const active = isFocused || isOpen;

    return (
        <OuterWrapper ref={containerRef} $isFocused={active}>
            <FixedLabel $isFocused={active} $error={!!error}>
                {label}
                {required && <RequiredMark>*</RequiredMark>}
            </FixedLabel>

            <SelectBox
                onClick={() => { setIsOpen(!isOpen); setIsFocused(true); }}
                $isFocused={active}
                $error={!!error}
            >
                <SelectedValue>
                    {selectedTz ? (
                        <>
                            <Globe size={15} color="#A0A8B4" />
                            <NameText>{selectedTz.label}</NameText>
                        </>
                    ) : (
                        <Placeholder>{t('common.choose', { defaultValue: 'Seçin' })}...</Placeholder>
                    )}
                </SelectedValue>
                <ActionButtons>
                    {value && (
                        <ClearBtn onClick={handleClear}>
                            <X size={14} color="#A0A8B4" />
                        </ClearBtn>
                    )}
                    <ChevronDown size={16} color="#A0A8B4" />
                </ActionButtons>
            </SelectBox>

            {error && <ErrorText>{error}</ErrorText>}

            {isOpen && (
                <Dropdown $direction={direction}>
                    <SearchWrapper>
                        <Search size={15} color="#A0A8B4" />
                        <SearchInput
                            autoFocus
                            placeholder={t('common.search', { defaultValue: 'Ara' }) + '...'}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </SearchWrapper>
                    <List>
                        {filteredTimezones.length > 0 ? (
                            filteredTimezones.map((tz) => (
                                <ListItem
                                    key={tz.id}
                                    onClick={() => {
                                        onChange?.(tz.id);
                                        setIsOpen(false);
                                        setIsFocused(false);
                                        setSearchTerm('');
                                    }}
                                    $isSelected={value === tz.id}
                                >
                                    <span>{tz.label}</span>
                                    {value === tz.id && <Check size={15} color="#0291EF" />}
                                </ListItem>
                            ))
                        ) : (
                            <NoResult>{t('common.noResults', { defaultValue: 'Sonuç bulunamadı' })}</NoResult>
                        )}
                    </List>
                </Dropdown>
            )}
        </OuterWrapper>
    );
};

export default TimezoneSelector;

const OuterWrapper = styled.div`
  position: relative;
  width: 100%;
  z-index: ${p => p.$isFocused ? 9999 : 1};
`;

const FixedLabel = styled.label`
  position: absolute;
  top: -8px;
  left: 12px;
  font-size: 11px;
  font-weight: 600;
  font-family: 'Nunito Sans', sans-serif;
  padding: 0 4px;
  background-color: #fff;
  color: ${p => p.$error ? '#E53935' : p.$isFocused ? '#0291EF' : '#A0A8B4'};
  z-index: 2;
  pointer-events: none;
  transition: color 0.2s ease;
`;

const RequiredMark = styled.span`
  color: #E53935;
  margin-left: 2px;
`;

const SelectBox = styled.div`
  height: 48px;
  border: 1.5px solid ${p => p.$error ? '#E53935' : p.$isFocused ? '#0291EF' : '#E0E4E8'};
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 14px;
  cursor: pointer;
  background-color: #fff;
  box-shadow: ${p => p.$isFocused ? '0 0 0 3px rgba(2, 145, 239, 0.1)' : 'none'};
  transition: all 0.2s;

  &:hover {
    border-color: ${p => p.$error ? '#E53935' : p.$isFocused ? '#0291EF' : '#C0C8D0'};
  }
`;

const SelectedValue = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
`;

const NameText = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  color: #004242;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Placeholder = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  color: #A0A8B4;
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const ClearBtn = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3px;
  border-radius: 50%;
  transition: background 0.15s;
  &:hover { background-color: #F5F7FA; }
`;

const Dropdown = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 8px 32px rgba(0, 66, 66, 0.12);
  border: 1px solid #E8ECF0;
  overflow: hidden;
  z-index: 10000;

  ${p => p.$direction === 'top' ? `
    bottom: 100%;
    margin-bottom: 6px;
  ` : `
    top: 100%;
    margin-top: 6px;
  `}
`;

const SearchWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 14px;
  border-bottom: 1px solid #E8ECF0;
  gap: 8px;
`;

const SearchInput = styled.input`
  border: none;
  outline: none;
  width: 100%;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  color: #004242;
  background: transparent;
  &::placeholder { color: #A0A8B4; }
`;

const List = styled.div`
  max-height: 240px;
  overflow-y: auto;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: #E0E4E8; border-radius: 4px; }
`;

const ListItem = styled.div`
  padding: 9px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  color: #004242;
  background-color: ${p => p.$isSelected ? 'rgba(2, 145, 239, 0.06)' : 'transparent'};
  &:hover { background-color: rgba(2, 145, 239, 0.04); }
`;

const NoResult = styled.div`
  padding: 16px;
  text-align: center;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  color: #A0A8B4;
`;

const ErrorText = styled.span`
  display: block;
  margin-top: 4px;
  font-size: 11px;
  font-family: 'Nunito Sans', sans-serif;
  color: #E53935;
`;
